import { db } from './firebase/config';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import JSZip from 'jszip';

const mapMeetingToSupabase = (meeting) => {
  return {
    id: meeting.id,
    user_id: meeting.userId || null,
    title: meeting.title || '',
    description: meeting.description || null,
    audio_file_url: null,
    audio_file_name: meeting.fileName || null,
    audio_duration_seconds: meeting.duration || null,
    status: meeting.status || null,
    transcription_id: meeting.transcriptionId || null,
    language: meeting.language || 'fr',
    created_at: meeting.createdAt instanceof Date
      ? meeting.createdAt.toISOString()
      : meeting.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
    updated_at: meeting.updatedAt instanceof Date
      ? meeting.updatedAt.toISOString()
      : meeting.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
    speaker_detection_enabled: true,
    participants: meeting.participants || [],
    default_template_id: null,
    meeting_date: meeting.date
      ? new Date(meeting.date).toISOString()
      : meeting.createdAt instanceof Date
        ? meeting.createdAt.toISOString()
        : meeting.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
    auto_send_summary_email: true,
    gdpr_consent: true,
    gdpr_consent_date: meeting.createdAt instanceof Date
      ? meeting.createdAt.toISOString()
      : meeting.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
    meeting_context: meeting.context || null,
    project_id: meeting.projectId || null
  };
};

const mapTranscriptionToSupabase = (transcription, meetingId) => {
  if (!transcription) return null;

  return {
    id: transcription.id,
    meeting_id: meetingId,
    assemblyai_id: transcription.assemblyaiId || null,
    text: transcription.text || transcription.utterances?.map(u => u.text).join('\n') || null,
    confidence: transcription.confidence || null,
    words: transcription.words || null,
    utterances: transcription.utterances || null,
    speakers: transcription.speakerMapping || transcription.speakers || null,
    created_at: transcription.createdAt instanceof Date
      ? transcription.createdAt.toISOString()
      : transcription.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
    updated_at: transcription.updatedAt instanceof Date
      ? transcription.updatedAt.toISOString()
      : transcription.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
    entities: transcription.entities || null,
    key_phrases: transcription.keyPhrases || null
  };
};

const mapSummaryToSupabase = (summary, meetingId) => {
  if (!summary) return null;

  return {
    id: summary.id,
    meeting_id: meetingId,
    content: summary.text || summary.content || null,
    template_id: summary.templateId || null,
    key_points: summary.keyPoints || null,
    action_items: summary.actionItems || null,
    decisions: summary.decisions || null,
    next_steps: summary.nextSteps || null,
    created_at: summary.createdAt instanceof Date
      ? summary.createdAt.toISOString()
      : summary.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
    updated_at: summary.updatedAt instanceof Date
      ? summary.updatedAt.toISOString()
      : summary.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
    keywords: summary.keywords || null
  };
};

const exportService = {
  getMeetingsByUserIds: async (userIds) => {
    try {
      const allMeetings = [];

      for (const userId of userIds) {
        const meetingsRef = collection(db, 'meetings');
        const q = query(
          meetingsRef,
          where('userId', '==', userId),
          orderBy('createdAt', 'desc')
        );

        const querySnapshot = await getDocs(q);
        querySnapshot.forEach(doc => {
          const data = doc.data();
          allMeetings.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date()
          });
        });
      }

      return allMeetings;
    } catch (error) {
      console.error('[ExportService] Erreur lors de la récupération des réunions:', error);
      throw error;
    }
  },

  getTranscriptionByMeetingId: async (meetingId) => {
    try {
      const transcriptionsRef = collection(db, 'transcriptions');
      const q = query(transcriptionsRef, where('meetingId', '==', meetingId));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        };
      }
      return null;
    } catch (error) {
      console.error('[ExportService] Erreur lors de la récupération de la transcription:', error);
      return null;
    }
  },

  getSummaryByTranscriptionId: async (transcriptionId) => {
    try {
      if (!transcriptionId) return null;

      const summariesRef = collection(db, 'summaries');
      const q = query(summariesRef, where('transcriptionId', '==', transcriptionId));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        };
      }
      return null;
    } catch (error) {
      console.error('[ExportService] Erreur lors de la récupération du résumé:', error);
      return null;
    }
  },

  exportMeetingsToZip: async (userIds, onProgress) => {
    try {
      const zip = new JSZip();

      if (onProgress) onProgress({ step: 'fetching_meetings', progress: 0 });

      const meetings = await exportService.getMeetingsByUserIds(userIds);
      const totalMeetings = meetings.length;

      if (totalMeetings === 0) {
        return { success: false, message: 'Aucune réunion trouvée pour les utilisateurs sélectionnés' };
      }

      for (let i = 0; i < meetings.length; i++) {
        const meeting = meetings[i];

        if (onProgress) {
          onProgress({
            step: 'processing',
            progress: Math.round((i / totalMeetings) * 100),
            current: i + 1,
            total: totalMeetings,
            meetingTitle: meeting.title
          });
        }

        const transcription = await exportService.getTranscriptionByMeetingId(meeting.id);
        const summary = transcription
          ? await exportService.getSummaryByTranscriptionId(transcription.id)
          : null;

        const exportData = {
          meeting: mapMeetingToSupabase(meeting),
          transcription: mapTranscriptionToSupabase(transcription, meeting.id),
          summary: mapSummaryToSupabase(summary, meeting.id)
        };

        const safeTitle = (meeting.title || 'reunion')
          .replace(/[^a-zA-Z0-9àâäéèêëïîôöùûüç\s-]/g, '')
          .replace(/\s+/g, '_')
          .substring(0, 50);

        const fileName = `${safeTitle}_${meeting.id.substring(0, 8)}.json`;

        zip.file(fileName, JSON.stringify(exportData, null, 2));
      }

      if (onProgress) onProgress({ step: 'generating_zip', progress: 100 });

      const zipBlob = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      });

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
      const fileName = `export_reunions_${timestamp}.zip`;

      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return {
        success: true,
        message: `${totalMeetings} réunion(s) exportée(s) avec succès`,
        count: totalMeetings
      };
    } catch (error) {
      console.error('[ExportService] Erreur lors de l\'export:', error);
      return {
        success: false,
        message: `Erreur lors de l'export: ${error.message}`
      };
    }
  }
};

export default exportService;
